/**
 * The roster member as the public read hands it over, and the derivations every
 * surface that renders one needs.
 *
 * Split out of `CreatorTeamSection` when that file passed 800 lines. Nothing
 * here decides anything — all filtering, liveness and player-source resolution
 * happen server-side in `/drops/slug/:slug/roster`, and these functions only
 * read what came back. That is the invariant that keeps the page and the counts
 * from ever disagreeing, so resist adding a rule to this file.
 */

import React from "react";
import { FaTwitch, FaYoutube } from "react-icons/fa";
import { SiKick } from "react-icons/si";
import { type InlinePlayerSource } from "./StreamInlinePlayer";
import { type DropRosterMember, type RosterHighlight } from "@devour/client";
import {formatCompactNumber} from "@/utils/formatCompactNumber";

/**
 * A roster member, as the API defines it.
 *
 * An ALIAS, not a copy. This was a hand-written interface mirroring
 * `DropRosterMember` field for field — the pattern the repo bans, because a
 * local mirror keeps compiling while the schema moves and the drift only
 * surfaces at runtime. Relevant right now: the off-game suppression made
 * several of these optional, which a mirror would have hidden.
 */
export type RosterMember = DropRosterMember;

/*
 * `platform` is OPTIONAL — a roster member is not required to have a channel,
 * and platform is a property of a channel. The existing `?.` inside already
 * handled an absent value at runtime; the signature simply did not admit it,
 * so a bare member's card could not call this without a cast. Returns null for
 * an absent platform, exactly as it already did for an unrecognised one.
 */
export function platformIcon(platform?: string): React.ReactNode {
    switch (platform?.toUpperCase()) {
        case "TWITCH": return <FaTwitch aria-hidden="true" />;
        case "YOUTUBE": return <FaYoutube aria-hidden="true" />;
        case "KICK": return <SiKick aria-hidden="true" />;
        default: return null;
    }
}

export function channelUrl(m: RosterMember): string | undefined {
    if (m.twitchChannelId) return `https://twitch.tv/${m.twitchChannelId}`;
    if (m.kickChannelSlug) return `https://kick.com/${m.kickChannelSlug}`;
    if (m.youtubeChannelId) return `https://youtube.com/${m.youtubeChannelId.startsWith("@") ? "" : "channel/"}${m.youtubeChannelId}`;
    return undefined;
}

/**
 * Where a roster card goes when it is NOT playable.
 *
 * A live creator does not go anywhere — they play in place. This covers the
 * offline case, and the live-on-YouTube case where we hold the channel but not
 * the current broadcast id. Those lead to the creator's own Drop, which is the
 * other thing a publisher roster is for: driving traffic to the creators on
 * it, and keeping the visitor on the platform. Only creators with no Drop at
 * all fall through to their channel on the originating platform.
 */
export function cardUrl(m: RosterMember): string | undefined {
    if (m.dropSlug) return `/drop-in/${m.dropSlug}`;
    return channelUrl(m);
}

/**
 * Stable per-member key — the member's OWN id.
 *
 * A roster member is a record on the team, not a channel; contestants qualify
 * onto a roster without streaming at all. This used to build an identity out
 * of channel fields and fall back to `${platform}-${label}`, so a roster of
 * members with neither — Road to the Finals' finalists — gave every one of
 * them the identical key `"undefined-"`. React then omitted siblings, and
 * because this key is also the SELECTION identity, every card resolved to the
 * first member.
 *
 * The channel fallbacks remain for rosters read through paths that predate
 * `id` on the wire; they are wrong only when nothing unique exists, which is
 * exactly what `id` now covers.
 */
export function memberKey(m: RosterMember): string {
    if (m.id) {
        return m.id;
    }
    const unique = m.dropId ?? m.twitchChannelId ?? m.kickChannelSlug ?? m.youtubeChannelId;
    return unique ? `${m.platform}-${unique}` : `${m.platform}-${m.label}`;
}

/**
 * The player source the SERVER resolved, not one rebuilt here.
 *
 * This used to construct the source from raw channel ids, which got Twitch
 * wrong: the embed keys on the broadcaster LOGIN, and some featured streams
 * persist `twitchChannelId` as the numeric id, which loads a black screen. The
 * server already resolves that (falling back to the Twitch social's username)
 * for every other Drop-In card, so the roster reads the same field instead of
 * reintroducing the bug. Absence means "not embeddable".
 */
export function playerSource(m: RosterMember): InlinePlayerSource | undefined {
    if (!m.playerSource) return undefined;
    /*
     * NO CAST. `m.playerSource` is the generated `DirectoryCardPlayerSource`
     * and the return type is the player's own `InlinePlayerSource`; the object
     * satisfies it structurally, so `as InlinePlayerSource` was buying nothing
     * except silence about the next time the two shapes diverge. That is the
     * same assertion this branch removed from `buildInlineSource` and
     * `ChallengesSection`, for the same reason.
     */
    return {...m.playerSource, title: m.streamTitle};
}

export function initials(label: string): string {
    return label.replace(/[^A-Za-z0-9 ]/g, "").split(/\s+/)
        .map(w => w[0])
        .join("")
        .slice(0, 2)
        .toUpperCase() || "—";
}

/** Clip runtimes are seconds and always short — m:ss is the whole range. */
export function formatDuration(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * A URL safe to drop into a CSS `url()`.
 *
 * Quoted, with any embedded quote percent-encoded, so a signed CDN URL cannot
 * break out of the declaration. Mirrors the helpers in `LevelRewardsDrawer` and
 * `PublisherHero`.
 */
export const safeCssUrl = (url: string): string => `url("${url.replace(/"/g, "%22")}")`;

export function compact(n: number): string {
    return formatCompactNumber(n);
}

/**
 * The clip belonging to one roster member, if the payload carries one.
 *
 * Matched by `creatorLabel`, and only that — `RosterHighlightsService` stamps
 * every highlight with the member's display name, and that is the one field
 * every highlight is guaranteed to carry. `creatorDropSlug` is optional ("when
 * one is linked"), so preferring it would pick a different clip than this rule
 * for a member with a highlight but no linked Drop — every caller must use
 * this, not its own match, or two surfaces can show two different clips for
 * the same person.
 */
export function highlightForMember(
    m: Pick<RosterMember, "label">,
    highlights: readonly RosterHighlight[],
): RosterHighlight | undefined {
    return highlights.find(h => h.creatorLabel === m.label);
}
